import { Box, Button, Checkbox, Flex } from "@chakra-ui/react";
import { FunctionComponent, useMemo } from "react";
import { CustomSelect } from "src/components/Inputs/CustomSelect";
import { SearchInput } from "src/components/Inputs/SearchInput";
import {
  AvailabilityFilters,
  EMPTY_FILTERS,
  hasActiveFilters,
} from "./filters";

type FilterItem = { key: string; label: string };

type Props = {
  filters: AvailabilityFilters;
  onChange: (filters: AvailabilityFilters) => void;
  gradeLabels: string[];
  programLabels: string[];
  showPrograms: boolean;
};

function toItems(labels: string[], allLabel: string): FilterItem[] {
  return [
    { key: "", label: allLabel },
    ...labels.map((label) => ({ key: label, label })),
  ];
}

export const AvailabilityFilterBar: FunctionComponent<Props> = ({
  filters,
  onChange,
  gradeLabels,
  programLabels,
  showPrograms,
}) => {
  const update = (patch: Partial<AvailabilityFilters>) =>
    onChange({ ...filters, ...patch });

  const gradeItems = useMemo(
    () => toItems(gradeLabels, "All grades"),
    [gradeLabels]
  );
  const programItems = useMemo(
    () => toItems(programLabels, "All programs"),
    [programLabels]
  );

  const selectedGrade =
    gradeItems.find((item) => item.key === filters.gradeLabel) ??
    gradeItems[0] ??
    null;
  const selectedProgram =
    programItems.find((item) => item.key === filters.programLabel) ??
    programItems[0] ??
    null;

  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      gap={3}
      width="100%"
      alignItems={{ base: "stretch", md: "flex-end" }}
    >
      <Flex maxWidth={{ base: "100%", md: "20rem" }} width="100%" flex="none">
        <SearchInput
          label="Schools"
          placeholder="Search schools"
          value={filters.search}
          onSearchChange={(search) => update({ search })}
          variant="leftIcon"
          inputProps={{
            "aria-label": "Search schools",
            // CustomSelect sizes its control from padding and icon rather than
            // a fixed height, leaving it 2px taller than a standard input.
            height: "calc(2.5rem + 2px)",
          }}
        />
      </Flex>

      <Box maxWidth={{ base: "100%", md: "12rem" }} width="100%" flex="none">
        <CustomSelect<FilterItem>
          label="Grade"
          items={gradeItems}
          selectedItem={selectedGrade}
          onChange={(item) => update({ gradeLabel: item?.key ?? "" })}
          renderItem={(item) => item.label}
          renderSelectedItem={(item) => item?.label ?? "All grades"}
        />
      </Box>

      {showPrograms && programLabels.length > 0 ? (
        <Box maxWidth={{ base: "100%", md: "12rem" }} width="100%" flex="none">
          <CustomSelect<FilterItem>
            label="Program"
            items={programItems}
            selectedItem={selectedProgram}
            onChange={(item) => update({ programLabel: item?.key ?? "" })}
            renderItem={(item) => item.label}
            renderSelectedItem={(item) => item?.label ?? "All programs"}
          />
        </Box>
      ) : null}

      <Checkbox
        flex="none"
        paddingBottom={2}
        isChecked={filters.onlyAvailable}
        onChange={(event) => update({ onlyAvailable: event.target.checked })}
      >
        Show only available seats
      </Checkbox>

      {hasActiveFilters(filters) ? (
        <Button
          variant="outline"
          colorScheme="gray"
          flex="none"
          onClick={() => onChange(EMPTY_FILTERS)}
        >
          Clear
        </Button>
      ) : null}
    </Flex>
  );
};
